import React, { FC } from 'react';
import { GenExternalLink, GenNavTile } from '@/components';
import { TypeAccountNavTileFields } from '@/core/generated-types';
import {
  SubEngineInstallmentStatusEnum,
  SubEngineSubscriptionStatusEnum,
  TypeSubEngineSubscription,
} from '@/lib/api/subscriptionManager/types';
import { isSubEngineSubscriptionActive } from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import NextBoxDetails from './NextBoxDetails';
import { _findChildForSubscription } from '../utils';

export type ManageERCSubscriptionsTileProps = {
  t: TypeAccountSettingsPageTranslations;
  subscriptions: TypeSubEngineSubscription[];
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
};

type ManageERCSubscriptionProps = {
  subscription: TypeSubEngineSubscription;
};

const ManageSubscriptionsTile: FC<ManageERCSubscriptionsTileProps> = ({
  t,
  subscriptions,
  tile,
  locale,
}) => {
  if (!subscriptions || subscriptions.length === 0) return null;

  const NoUpcomingScheduledItemsNotice: FC = () => {
    return (
      <p className="md:text-base text-sm">{t.noUpcomingInstallmentsMessage}</p>
    );
  };

  const ManageSubscription: FC<ManageERCSubscriptionProps> = ({
    subscription,
  }) => {
    const nextUnprocessedItem = subscription.installments.find(
      (box) => box.status === SubEngineInstallmentStatusEnum.SCHEDULED,
    );

    const hasUpcomingItems =
      isSubEngineSubscriptionActive(subscription) && nextUnprocessedItem;

    const isCancelled =
      subscription.status === SubEngineSubscriptionStatusEnum.CANCELLED;

    const ctaAction = isCancelled ? `reactivate` : `skip-cancel`;

    const ctaLabel = isCancelled
      ? t.reactivateERCSubscriptionCta
      : t.skipCancelERCSubscriptionCta;

    return (
      <div className="mb-8 border-b border-grey-midtone pb-8 last:mb-0 last:border-none last:pb-0">
        <p className="mb-4 text-xl font-bold">{t.childsBoxes}</p>
        {hasUpcomingItems ? (
          <NextBoxDetails
            t={t}
            locale={locale}
            nextUnprocessedItem={nextUnprocessedItem}
          />
        ) : (
          <NoUpcomingScheduledItemsNotice />
        )}
        <div>
          <div className="mt-2 flex flex-row flex-wrap gap-6 md:text-base text-sm">
            <GenExternalLink
              href={`/account/early-reader-club/${subscription.id}/${ctaAction}`}
              cta={ctaLabel}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <GenNavTile
      title={tile.title}
      subTitle={tile.subheader}
      description={tile.description}
    >
      {subscriptions.map((subscription) => (
        <ManageSubscription subscription={subscription} key={subscription.id} />
      ))}
    </GenNavTile>
  );
};

export default React.memo(ManageSubscriptionsTile);
